import { useCallback } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { activityQueryKeys } from 'services/api/activity';
import { admissionQueryKeys } from 'services/api/admissions';
import { attachmentsQueryKeys } from 'services/api/attachments';
import { dischargeCalendarQueryKeys } from 'services/api/dischargeCalendar';
import { episodeQueryKeys } from 'services/api/episode';
import { progressChartQueryKeys } from 'services/api/locationEpisode/progressChart';
import { locationEpisodeQueryKeys } from 'services/api/locationEpisodes';
import { authorizationQueryKeys, referralQueryKeys } from 'services/api/reviews';
import { summarizationQueryKeys } from 'services/api/summarizations';

export type InvalidateOptions = {
  episode?: boolean | Record<string, any>;
  episodes?: boolean;
  locationEpisode?: boolean | Record<string, any>;
  authorizations?: boolean | Record<string, any>;
  authorization?: boolean | Record<string, any>;
  referrals?: boolean;
  referral?: boolean | Record<string, any>;
  activities?: boolean;
  attachments?: boolean | Record<string, any>;
  attachmentsCount?: boolean | Record<string, any>;
  progressChart?: boolean;
  summarizations?: boolean;
  summarization?: boolean | Record<string, any>;
  admissions?: boolean;
};

const getParams = (option: boolean | Record<string, any>) => (typeof option === 'object' ? { ...option } : {});

export default function useInvalidateQueries() {
  const queryClient = useQueryClient();

  return useCallback(
    ({
      episode = false,
      episodes = false,
      locationEpisode = false,
      authorizations = false,
      authorization = false,
      referrals = false,
      referral = false,
      activities = false,
      attachments = false,
      attachmentsCount = false,
      progressChart = false,
      summarizations = false,
      summarization = false,
      admissions = false,
    }: InvalidateOptions = {}) => {
      if (episode) queryClient.invalidateQueries({ queryKey: episodeQueryKeys.show(getParams(episode)) });
      if (episodes) {
        queryClient.invalidateQueries({ queryKey: episodeQueryKeys.base });
        queryClient.invalidateQueries({ queryKey: dischargeCalendarQueryKeys.base });
      }
      if (locationEpisode) {
        queryClient.invalidateQueries({ queryKey: locationEpisodeQueryKeys.show(getParams(locationEpisode)) });
        if (progressChart)
          queryClient.invalidateQueries({ queryKey: progressChartQueryKeys.show(getParams(locationEpisode).id) });
      }
      if (authorizations)
        queryClient.invalidateQueries({ queryKey: authorizationQueryKeys.index(getParams(authorizations)) });
      if (authorization)
        queryClient.invalidateQueries({ queryKey: authorizationQueryKeys.show(getParams(authorization)) });
      if (referrals) queryClient.invalidateQueries({ queryKey: referralQueryKeys.base }); // invalidates referrals list page AND all patient story referrals
      if (referral) queryClient.invalidateQueries({ queryKey: referralQueryKeys.show(getParams(referral)) });
      if (activities) queryClient.invalidateQueries({ queryKey: activityQueryKeys.base });
      if (attachments) queryClient.invalidateQueries({ queryKey: attachmentsQueryKeys.index(getParams(attachments)) });
      if (attachmentsCount)
        queryClient.invalidateQueries({ queryKey: attachmentsQueryKeys.count(getParams(attachmentsCount)) });
      if (summarizations) queryClient.invalidateQueries({ queryKey: summarizationQueryKeys.base });
      if (summarization)
        queryClient.invalidateQueries({ queryKey: summarizationQueryKeys.show(getParams(summarization)) });
      if (admissions) queryClient.invalidateQueries({ queryKey: admissionQueryKeys.base });
    },
    [queryClient]
  );
}
